import React from "react";
import { CheckCircle2, Clock, XCircle, Send, Ban, MinusCircle, Mail, MessageCircle } from "lucide-react";

export const CHANNEL_LABEL = {
    email: "Email",
    sms: "SMS",
    whatsapp: "WhatsApp",
};

export const TRIGGER_LABEL = {
    booking_created: "Booking created",
    booking_confirmed: "Booking confirmed",
    booking_rescheduled: "Booking rescheduled",
    booking_cancelled: "Booking cancelled",
    deposit_requested: "Deposit requested",
    deposit_paid: "Deposit paid",
    deposit_overdue: "Deposit overdue",
    forms_required: "Forms required",
    forms_incomplete: "Forms incomplete",
    forms_completed: "Forms completed",
    reminder_before_appointment: "Reminder before appointment",
    appointment_completed: "Appointment completed",
    aftercare_due: "Aftercare due",
    review_request_due: "Review request due",
    rebooking_reminder_due: "Rebooking reminder",
    birthday: "Birthday",
    inactive_customer_winback: "Inactive win-back",
    manual: "Manual send",
};

export const CATEGORY_LABEL = {
    transactional: "Transactional",
    operational: "Operational",
    aftercare: "Aftercare",
    review_request: "Review request",
    rebooking: "Rebooking",
    marketing: "Marketing",
    manual: "Manual",
};

export const STATUS_STYLES = {
    draft:      "bg-white/10 border-white/25 text-white/80",
    pending:    "bg-white/10 border-white/25 text-white/80",
    scheduled:  "bg-blue-500/15 border-blue-400/40 text-blue-100",
    processing: "bg-indigo-500/15 border-indigo-400/40 text-indigo-100",
    sent:       "bg-emerald-500/15 border-emerald-400/40 text-emerald-100",
    failed:     "bg-red-500/15 border-red-400/40 text-red-100",
    cancelled:  "bg-amber-500/10 border-amber-400/40 text-amber-100",
    skipped:    "bg-white/5 border-white/15 text-white/50",
    published:  "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
    inactive:   "bg-amber-500/15 border-amber-400/40 text-amber-200",
    archived:   "bg-red-500/10 border-red-400/30 text-red-200",
};

// Friendly (non-technical) status labels for the salon-owner UI. Advanced mode
// still shows the raw dev-facing labels via <Pill value={rawStatus} />.
const FRIENDLY = {
    draft:      "Draft",
    pending:    "Waiting",
    scheduled:  "Scheduled",
    processing: "Sending",
    sent:       "Sent",
    failed:     "Failed",
    cancelled:  "Cancelled",
    skipped:    "Skipped",
};

export function friendlyStatus(s) { return FRIENDLY[s] || s; }

export function statusIcon(s) {
    const cls = "h-3 w-3";
    switch (s) {
        case "sent":      return <CheckCircle2 className={`${cls} text-emerald-300`} strokeWidth={2} />;
        case "scheduled":
        case "pending":
        case "processing":return <Clock className={`${cls} text-blue-300`} strokeWidth={2} />;
        case "failed":    return <XCircle className={`${cls} text-red-300`} strokeWidth={2} />;
        case "cancelled": return <Ban className={`${cls} text-amber-300`} strokeWidth={2} />;
        case "skipped":   return <MinusCircle className={`${cls} text-white/40`} strokeWidth={2} />;
        default:          return <Send className={`${cls} text-white/50`} strokeWidth={2} />;
    }
}

export function Pill({ value, label }) {
    const cls = STATUS_STYLES[value] || "bg-white/5 border-white/10 text-white/70";
    return (
        <span className={`inline-flex text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${cls}`}>
            {label || value}
        </span>
    );
}

export function formatDateTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
}


// ─── Shared Delivery Selector ───────────────────────────────────────
export function DeliverySegmented({ value, onChange, busy, testIdPrefix, emailAvailable, waAvailable, waReady, waPreparing, waAttention }) {
    // Only allow WhatsApp / Both when WhatsApp is globally connected AND
    // this automation's Meta template is APPROVED (or the current value is
    // already WhatsApp/Both so Angela can still see her current selection).
    const canWa = waAvailable && waReady;
    const canBoth = emailAvailable && canWa;
    const opts = [
        { key: "email",    label: "Email",    disabled: !emailAvailable && value !== "email" },
        { key: "whatsapp", label: "WhatsApp", disabled: !canWa && value !== "whatsapp" },
        { key: "both",     label: "Both",     disabled: !canBoth && value !== "both" },
    ];
    // Small WhatsApp status pill
    const waPill = waPreparing ? "Preparing"
                 : waAttention ? "Needs attention"
                 : !waAvailable ? "Not connected"
                 : !waReady ? "Not available"
                 : null;
    return (
        <div className="flex items-center gap-1.5" data-testid={testIdPrefix}>
            {opts.map((o) => {
                const selected = value === o.key;
                const isWa = o.key === "whatsapp" || o.key === "both";
                return (
                    <button
                        key={o.key}
                        type="button"
                        onClick={() => !busy && !o.disabled && !selected && onChange(o.key)}
                        disabled={busy || o.disabled}
                        data-testid={`${testIdPrefix}-${o.key}`}
                        className={`flex-1 h-9 rounded-full text-[12px] font-medium border transition-colors relative inline-flex items-center justify-center gap-1.5 ${
                            selected
                                ? "bg-gold text-navy border-gold"
                                : o.disabled
                                    ? "hairline text-white/30 cursor-not-allowed"
                                    : "hairline text-white/70 hover:text-gold hover:border-gold/40"
                        }`}
                    >
                        {o.key === "email" && <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        {o.key === "whatsapp" && <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        {o.key === "both" && <span className="text-[10px]">+</span>}
                        <span>{o.label}</span>
                        {isWa && waPill && (
                            <span
                                className={`ml-1 text-[9px] uppercase tracking-[0.14em] px-1.5 py-[1px] rounded-full border ${
                                    selected ? "border-navy/40 text-navy/70"
                                             : waAttention ? "border-amber-400/40 text-amber-200"
                                                           : "border-white/20 text-white/50"
                                }`}
                                data-testid={`${testIdPrefix}-${o.key}-pill`}
                            >
                                {waPill}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}


// ─── Shared ToggleSwitch ───────────────────────────────────────────────
// Single source-of-truth for every switch on the Communications page.
// Sprint 36.4 fix: use explicit `left-[…]` positioning (NOT translate-x)
// so the thumb is guaranteed to sit fully inside the track regardless of
// any ancestor transform / flex-compression side-effects. Track width /
// thumb width geometry is fixed with pixel values so it can't drift.
//
//   Geometry:
//     track w=44 h=24 border=1 → padding-box 42×22
//     thumb w=18 h=18
//     OFF thumb left=2 → occupies 2..20 (2px right gap)
//     ON  thumb left=22 → occupies 22..40 (2px right gap)
//     top=2 (2px inset top+bottom)
export function CommsToggleSwitch({ on, onToggle, busy, testId, ariaLabel }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={!!on}
            aria-label={ariaLabel}
            onClick={() => !busy && onToggle(!on)}
            disabled={busy}
            data-testid={testId}
            className={`relative inline-block h-6 w-11 rounded-full transition-colors duration-200 shrink-0 border ${
                on ? "bg-gold border-gold" : "bg-white/15 border-white/25"
            } ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
        >
            <span
                aria-hidden="true"
                className={`absolute top-[2px] h-[18px] w-[18px] rounded-full bg-navy shadow-md transition-[left] duration-200 ${
                    on ? "left-[22px]" : "left-[2px]"
                }`}
            />
        </button>
    );
}
